/**
 * Section switcher — the app bar's left slot.
 *
 * The rail says which work object you are on; this says which of its screens
 * you are looking at, and moves between them in one click. It replaces the
 * former "Group › Section" breadcrumb, which repeated what the rail already
 * showed.
 *
 * Two renderings of the same model:
 *  - `md` and up: a pill row, one pill per screen. The active pill is the
 *    single solid accent of the shell.
 *  - below `md`: a dropdown button naming the current screen, since the app
 *    bar has no room for a row and the rail (a full-width overlay drawer on
 *    mobile) only reaches a section's first screen.
 *
 * A section with a single visible screen has nothing to switch, so it renders
 * as a plain title. That is the common case for every non-administrator role.
 *
 * Note: this diverges from the "app bar carries a breadcrumb only" rule of the
 * shared shell. Deliberate — the breadcrumb was redundant here, and putting
 * the switch in the bar keeps it on every screen of a section (lists, forms
 * and custom pages alike) without spending a second row of vertical space.
 */

import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, ButtonBase, ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useNavigate } from 'react-router-dom';
import type { NavItem, NavSection } from './navigation';

interface SectionSwitcherProps {
  section: NavSection;
  current: NavItem;
}

export const SectionSwitcher: React.FC<SectionSwitcherProps> = ({ section, current }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const navigate = useNavigate();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const labelOf = (item: NavItem): string =>
    translate(item.labelKey, { smart_count: item.smartCount ?? 1 });

  const title = (
    <Box
      component="span"
      sx={{
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {labelOf(current)}
    </Box>
  );

  // Nothing to switch between: the section is one screen for this role.
  if (section.items.length <= 1) {
    return <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>{title}</Box>;
  }

  if (isCompact) {
    return (
      <>
        <ButtonBase
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            minWidth: 0,
            height: '30px',
            padding: '0 8px',
            borderRadius: theme.custom.radius.full,
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }}
        >
          {title}
          <ExpandMoreRoundedIcon sx={{ fontSize: '18px', color: theme.custom.text.tertiary }} />
        </ButtonBase>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          {section.items.map((item) => (
            <MenuItem
              key={item.name}
              selected={item.name === current.name}
              onClick={() => {
                setAnchorEl(null);
                navigate(item.to);
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>{labelOf(item)}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <Box
      component="nav"
      aria-label={section.labelKey ? translate(section.labelKey) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        // A long section (four screens with long names) must scroll inside
        // this row rather than push the bar's right cluster off screen.
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {section.items.map((item) => {
        const isActive = item.name === current.name;
        return (
          <ButtonBase
            key={item.name}
            onClick={() => navigate(item.to)}
            aria-current={isActive ? 'page' : undefined}
            sx={{
              flexShrink: 0,
              height: '30px',
              padding: '0 14px',
              borderRadius: theme.custom.radius.full,
              fontSize: '0.8125rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
              color: isActive ? theme.palette.primary.contrastText : theme.custom.text.tertiary,
              backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
              '&:hover': {
                backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
                color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
              },
            }}
          >
            {labelOf(item)}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default SectionSwitcher;
